import { useState } from "react";
import { api, formatApiError, imgUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Edit3, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Field } from "./shared";

const EVENT_TYPES = [
  { v: "show", l: "Live Show" },
  { v: "keynote", l: "Keynote" },
  { v: "festival", l: "Festival" },
  { v: "appearance", l: "Appearance" },
  { v: "studio", l: "Studio Session" },
  { v: "tour", l: "Tour Date" },
  { v: "backstage", l: "Backstage · Live (Paid Members)" },
];

const EVENT_BLANK = {
  title: "",
  event_type: "show",
  venue: "",
  city: "",
  date: "",
  time: "",
  description: "",
  ticket_url: "",
  cover_image: "",
  featured: false,
  sold_out: false,
  status: "upcoming",
  backstage_url: "",
  is_paid_only: false,
};

const NEW_FORM_DEFAULTS = { ...EVENT_BLANK, _notify: true };

export function EventsPanel({ events, reload }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EVENT_BLANK);
  const [saving, setSaving] = useState(false);

  const startCreate = () => {
    setForm(NEW_FORM_DEFAULTS);
    setEditing(null);
    setShowForm(true);
  };
  const startEdit = (ev) => {
    setForm({
      ...EVENT_BLANK,
      ...ev,
      _notify: false, // don't re-blast on edits
    });
    setEditing(ev.id);
    setShowForm(true);
  };
  const cancel = () => {
    setShowForm(false);
    setEditing(null);
    setForm(NEW_FORM_DEFAULTS);
  };

  const update = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value ?? e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.date.trim()) {
      toast.error("Title and date are required.");
      return;
    }
    setSaving(true);
    try {
      const { _notify, ...payload } = form;
      // strip empty optionals
      ["venue", "city", "time", "description", "ticket_url", "cover_image"].forEach((k) => {
        if (!payload[k]) payload[k] = null;
      });
      if (editing) {
        const r = await api.put(`/admin/events/${editing}`, payload);
        const reminder = r.data?.backstage_reminder;
        if (reminder === "created") toast.success("Event updated · Backstage reminder scheduled (1h before)");
        else if (reminder === "updated") toast.success("Event updated · Backstage reminder rescheduled");
        else if (reminder === "cancelled") toast.success("Event updated · Backstage reminder cancelled");
        else toast.success("Event updated");
      } else {
        const r = await api.post(
          `/admin/events?notify_subscribers=${_notify ? 1 : 0}`,
          payload
        );
        const blastMsg = r.data?.blast_queued
          ? "Event added — Inner Circle blast queued"
          : "Event added (no blast sent)";
        const reminderMsg = r.data?.backstage_reminder === "created"
          ? " · Backstage reminder scheduled (1h before)"
          : "";
        toast.success(blastMsg + reminderMsg);
      }
      cancel();
      reload();
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this event permanently?")) return;
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success("Event deleted");
      reload();
    } catch (err) {
      toast.error(formatApiError(err) || "Delete failed");
    }
  };

  return (
    <div data-testid="events-panel" className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-sm text-white/55">{events.length} events on the calendar</p>
        {!showForm && (
          <Button
            onClick={startCreate}
            className="btn-gold text-xs uppercase tracking-[0.25em]"
            data-testid="event-create"
          >
            <Plus size={14} className="mr-1.5" />
            Add Event
          </Button>
        )}
      </div>

      {showForm && (
        <form
          onSubmit={save}
          className="surface p-7 lg:p-9 space-y-5"
          data-testid="event-form"
        >
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-serif text-2xl tracking-tighter">
              {editing ? "Edit Event" : "New Event"}
            </h3>
            <button
              type="button"
              onClick={cancel}
              className="text-white/55 hover:text-white"
              data-testid="event-form-cancel"
            >
              <X size={18} />
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Title *">
              <Input
                required
                value={form.title}
                onChange={update("title")}
                placeholder="Live At Soundstage Philly"
                className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                data-testid="event-title"
              />
            </Field>
            <Field label="Event Type *">
              <select
                value={form.event_type}
                onChange={update("event_type")}
                className="w-full bg-transparent border-0 border-b border-white/15 rounded-none focus:outline-none focus:border-[color:var(--gold)] px-0 py-2 text-sm text-white"
                data-testid="event-type"
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t.v} value={t.v} className="bg-[#0a050d]">
                    {t.l}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid sm:grid-cols-3 gap-5">
            <Field label="Date *">
              <Input
                type="date"
                value={
                  /^\d{4}-\d{2}-\d{2}$/.test(form.date)
                    ? form.date
                    : ""
                }
                onChange={update("date")}
                className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                data-testid="event-date"
              />
            </Field>
            <Field label="Time (optional)">
              <Input
                value={form.time || ""}
                onChange={update("time")}
                placeholder="8:00 PM EST"
                className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                data-testid="event-time"
              />
            </Field>
            <Field label="Status">
              <select
                value={form.status}
                onChange={update("status")}
                className="w-full bg-transparent border-0 border-b border-white/15 rounded-none focus:outline-none focus:border-[color:var(--gold)] px-0 py-2 text-sm text-white"
                data-testid="event-status"
              >
                <option value="upcoming" className="bg-[#0a050d]">Upcoming</option>
                <option value="past" className="bg-[#0a050d]">Past</option>
                <option value="cancelled" className="bg-[#0a050d]">Cancelled</option>
              </select>
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Venue">
              <Input
                value={form.venue || ""}
                onChange={update("venue")}
                placeholder="Soundstage"
                className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                data-testid="event-venue"
              />
            </Field>
            <Field label="City">
              <Input
                value={form.city || ""}
                onChange={update("city")}
                placeholder="Philadelphia, PA"
                className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                data-testid="event-city"
              />
            </Field>
          </div>

          <Field label="Description (optional)">
            <Textarea
              rows={3}
              maxLength={600}
              value={form.description || ""}
              onChange={update("description")}
              placeholder="Short blurb shown on the event card."
              className="bg-transparent border border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] resize-none"
              data-testid="event-description"
            />
          </Field>

          <Field label="Ticket URL (optional)">
            <Input
              value={form.ticket_url || ""}
              onChange={update("ticket_url")}
              placeholder="https://..."
              className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
              data-testid="event-ticket-url"
            />
          </Field>

          {form.event_type === "backstage" && (
            <>
              <Field label="Backstage Join URL (leave blank to use the default Be.Live link)">
                <Input
                  value={form.backstage_url || ""}
                  onChange={update("backstage_url")}
                  placeholder="https://app.be.live/115789/guest"
                  className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                  data-testid="event-backstage-url"
                />
              </Field>
              <p
                className="text-[10px] uppercase tracking-[0.22em] text-white/45 -mt-2 leading-relaxed"
                data-testid="event-backstage-reminder-hint"
              >
                🔔 A reminder email auto-goes to paid members 1 hour before this show. Cancels itself if you edit or delete the event.
              </p>
            </>
          )}

          <div className="flex flex-wrap gap-6 pt-2">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-white/75">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={update("featured")}
                className="accent-[color:var(--gold)]"
                data-testid="event-featured"
              />
              Featured (highlighted card)
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-white/75">
              <input
                type="checkbox"
                checked={form.sold_out}
                onChange={update("sold_out")}
                className="accent-[color:var(--gold)]"
                data-testid="event-sold-out"
              />
              Sold Out
            </label>
            {!editing && (
              <label className="flex items-center gap-2 cursor-pointer text-sm text-white/75">
                <input
                  type="checkbox"
                  checked={form._notify ?? true}
                  onChange={update("_notify")}
                  className="accent-[color:var(--gold)]"
                  data-testid="event-notify"
                />
                Email-blast Inner Circle
              </label>
            )}
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-white/5">
            <Button
              type="submit"
              disabled={saving}
              className="btn-gold text-xs uppercase tracking-[0.25em]"
              data-testid="event-save"
            >
              {saving ? "Saving..." : editing ? "Save Changes" : "Add Event"}
            </Button>
            <button
              type="button"
              onClick={cancel}
              className="text-xs uppercase tracking-[0.25em] text-white/55 hover:text-white px-3 py-2"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {events.length === 0 && !showForm && (
        <div className="surface p-12 text-center text-white/45 italic">
          No events on the calendar yet. Click <strong>Add Event</strong> to announce the next show.
        </div>
      )}

      <div className="space-y-3">
        {events.map((ev) => {
          const dateLabel = /^\d{4}-\d{2}-\d{2}/.test(ev.date)
            ? new Date(ev.date).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : ev.date;
          return (
            <div
              key={ev.id}
              className="surface p-5 lg:p-6 grid lg:grid-cols-12 gap-4 items-center"
              data-testid={`event-row-${ev.id}`}
            >
              <div className="lg:col-span-2">
                <div
                  className="font-serif text-2xl tracking-tighter"
                  style={{ color: "var(--gold)" }}
                >
                  {dateLabel}
                </div>
                {ev.time && (
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">
                    {ev.time}
                  </div>
                )}
              </div>
              <div className="lg:col-span-5 flex items-center gap-3">
                <EventFlyerUploader event={ev} reload={reload} />
                <div>
                  <p className="font-serif text-lg tracking-tight">{ev.title}</p>
                  <p className="text-xs text-white/55 mt-1">
                    {[ev.venue, ev.city].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
              </div>
              <div className="lg:col-span-3 flex flex-wrap gap-2">
                <span className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border border-white/10 text-white/65">
                  {ev.event_type}
                </span>
                {ev.featured && (
                  <span
                    className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border"
                    style={{
                      color: "var(--gold)",
                      borderColor: "rgba(232,180,184,0.5)",
                    }}
                  >
                    Featured
                  </span>
                )}
                {ev.sold_out && (
                  <span className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border border-red-400/40 text-red-300/90">
                    Sold Out
                  </span>
                )}
                {ev.status === "cancelled" && (
                  <span className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border border-white/10 text-white/45">
                    Cancelled
                  </span>
                )}
              </div>
              <div className="lg:col-span-2 flex justify-end gap-2">
                <button
                  onClick={() => startEdit(ev)}
                  className="text-[10px] uppercase tracking-[0.2em] text-white/65 hover:text-[color:var(--gold)] px-3 py-2"
                  data-testid={`event-edit-${ev.id}`}
                >
                  <Edit3 size={12} className="inline mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => remove(ev.id)}
                  className="text-[10px] uppercase tracking-[0.2em] text-red-400/70 hover:text-red-300 px-3 py-2"
                  data-testid={`event-delete-${ev.id}`}
                >
                  <Trash2 size={12} className="inline mr-1" />
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EventFlyerUploader({ event, reload }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/events/${event.id}/flyer`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Flyer uploaded.");
      if (reload) await reload();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Flyer upload failed");
    } finally {
      setBusy(false);
    }
  };
  const removeFlyer = async () => {
    try {
      await api.delete(`/admin/events/${event.id}/flyer`);
      toast.success("Flyer removed.");
      if (reload) await reload();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not remove flyer");
    }
  };
  return (
    <div className="shrink-0">
      <label
        className="relative block w-16 h-16 border border-white/10 cursor-pointer overflow-hidden group/flyer"
        data-testid={`event-flyer-upload-${event.id}`}
      >
        {event.flyer_url ? (
          <img src={imgUrl(event.flyer_url)} alt="Flyer" className="w-full h-full object-cover" />
        ) : (
          <span className="w-full h-full flex flex-col items-center justify-center gap-1 text-[8px] uppercase tracking-[0.15em] text-white/40">
            <ImageIcon size={16} /> Flyer
          </span>
        )}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={busy}
          onChange={(e) => upload(e.target.files?.[0])}
          className="hidden"
        />
        <span className="absolute inset-0 bg-black/55 opacity-0 group-hover/flyer:opacity-100 flex items-center justify-center text-[8px] uppercase tracking-[0.15em] text-[color:var(--gold)] transition-opacity">
          {busy ? "…" : event.flyer_url ? "Replace" : "Upload"}
        </span>
      </label>
      {event.flyer_url && (
        <button
          onClick={removeFlyer}
          className="text-[8px] uppercase tracking-[0.15em] text-white/40 hover:text-red-400 mt-1 w-full text-center"
          data-testid={`event-flyer-remove-${event.id}`}
        >
          Remove
        </button>
      )}
    </div>
  );
}
